/**
 * Config contract for {@link ContextMenuComponent} (`app-context-menu`) — the common kebab / "More actions"
 * menu. Everything is config: the items, the trigger button, and the panel styling. A feature declares a
 * `ContextMenuConfig` (usually in its `*-grid.config.ts` / `*.config.ts`) and binds a row as `context`;
 * the item predicates decide per row what shows, what is disabled, and what each label reads.
 */

/** Colour intent of a menu item. `danger` is the destructive action (Revoke, Delete, Reject). */
export type ContextMenuItemVariant = 'default' | 'primary' | 'danger' | 'success' | 'warning';

/** Which corner of the panel is pinned to which corner of the trigger. `-end` = right-aligned. */
export type ContextMenuPlacement = 'bottom-end' | 'bottom-start' | 'top-end' | 'top-start';

/** Trigger button look. `icon` is the bare kebab used in list rows; the rest render a labelled button. */
export type ContextMenuTriggerVariant = 'icon' | 'ghost' | 'outline' | 'solid';

/**
 * What opens the panel from the built-in trigger. `click` (default) toggles it; `hover` opens on pointer
 * enter and closes on leave, with a grace period so the pointer can cross the gap into the panel.
 *
 * `hover` is for a panel that REVEALS something (a summary, a preview). Keep `click` for a menu of actions:
 * a hover-opened panel is unreachable by touch, and its items can't be reached by keyboard from the trigger.
 */
export type ContextMenuOpenOn = 'click' | 'hover';

export type ContextMenuSize = 'sm' | 'md' | 'lg';

/**
 * One menu row. `visibleWhen` / `disabledWhen` / `labelWhen` receive the bound `context` (the row the menu
 * was opened for), so a single static config serves every row instead of a template full of `@if`s.
 */
export interface ContextMenuItem<T = unknown> {
  /** Stable key echoed back on select — switch on this in the handler. */
  id: string;
  label: string;
  /** `app-icon` name (see `icon-paths.ts`). Omit for a text-only item. */
  icon?: string;
  variant?: ContextMenuItemVariant;
  /** Right-aligned muted hint — a shortcut, count, or short qualifier. */
  hint?: string;
  /**
   * Where the hint sits. `'inline'` (default) right-aligns it on the label's row, which is right for
   * a shortcut or a count. `'below'` puts it under the label, which is what a SENTENCE needs — a
   * reason an item is unavailable competes with the label for the same row and loses.
   */
  hintPlacement?: 'inline' | 'below';
  /**
   * Lift this item out of the list as a tinted band, coloured by its `variant`.
   *
   * For the one action a row can actually take. In a list of eight it was the ninth thing to read;
   * as a band at the top it is the first, and the rest of the menu becomes what it is — everything
   * else.
   */
  emphasis?: 'band';
  /** Per-row hint (the counterpart to `labelWhen`) — for a value read off the bound row. Wins over `hint`. */
  hintWhen?: (context: T) => string;
  /** Render as a plain read-only row: no hover tint, not clickable, not a focus stop. */
  readonly?: boolean;
  /** Renders a tick on the left; use for toggles (`Show archived`). */
  checked?: boolean;
  /** Tooltip on the item — the place to explain WHY an item is disabled. */
  tooltip?: string;
  disabled?: boolean;
  hidden?: boolean;
  /** Draws a divider above this item (suppressed automatically when nothing precedes it). */
  separatorBefore?: boolean;
  /** Group heading above this item (suppressed with the item when it is hidden). */
  sectionLabel?: string;
  /** Arbitrary payload returned on select. */
  data?: unknown;
  /** Hide this item for the current row. Combined with `hidden` (either hides). */
  visibleWhen?: (context: T) => boolean;
  /** Disable this item for the current row. Combined with `disabled` (either disables). */
  disabledWhen?: (context: T) => boolean;
  /** Per-row label (e.g. Run vs Open depending on the task kind). Wins over `label`. */
  labelWhen?: (context: T) => string;
  /** Per-row icon, for an item whose meaning changes with the row. Wins over `icon`. */
  iconWhen?: (context: T) => string;
  /** Per-row variant — the tone follows the action when the action itself is derived. Wins over `variant`. */
  variantWhen?: (context: T) => ContextMenuItemVariant;
}

/** The button that opens the menu. Omit entirely for the kebab default. */
export interface ContextMenuTriggerConfig {
  /**
   * A CSS selector for the nearest ancestor whose RIGHT-CLICK opens this menu.
   *
   * For a row that already spends its left click on something else — opening a detail panel, say.
   * Two gestures, two results, and neither steals the other. The listener is installed on the
   * matched ancestor, so the whole row is the target rather than just the kebab.
   */
  contextMenuHost?: string;
  /** Default `'more'` (the vertical kebab). */
  icon?: string;
  iconSize?: number;
  /** Set to render a labelled button instead of an icon-only one. */
  label?: string;
  /** Default `'More actions'`. Set `''` to suppress. */
  tooltip?: string;
  /** Defaults to `label` or the tooltip. */
  ariaLabel?: string;
  variant?: ContextMenuTriggerVariant;
  size?: ContextMenuSize;
  /** Adds a chevron after the label — for labelled dropdown-style triggers. */
  showCaret?: boolean;
  disabled?: boolean;
  /** Extra class(es) on the trigger, for feature-specific spacing only. */
  cssClass?: string;
  /** Default `'click'`. See {@link ContextMenuOpenOn} before choosing `'hover'`. */
  openOn?: ContextMenuOpenOn;
  /**
   * Make the component's own PROJECTED CONTENT the trigger, instead of a button it renders.
   *
   * Pair with `[showTrigger]="false"` and put the markup inside `<app-context-menu>`: a click anywhere in
   * that content opens the panel at the pointer. For a whole row/card that should reveal a detail panel,
   * where a separate trigger button would be the wrong affordance. The feature wires no click handler.
   */
  openFromContent?: boolean;
  /**
   * Content mode: descendants whose clicks belong to them, not to the panel. Defaults to every native
   * control, so buttons and inputs inside the content keep working untouched.
   */
  contentIgnoreSelector?: string;
  /** Hover mode: ms the pointer must rest on the trigger before opening. Default `80`. */
  hoverOpenDelay?: number;
  /**
   * Hover mode: ms after the pointer leaves before closing. Default `160` — long enough to cross the gap
   * onto the panel, which cancels the close.
   */
  hoverCloseDelay?: number;
}

/**
 * Panel appearance. Every colour falls back to a brand token, so the defaults already match the app —
 * only override for a genuinely different surface.
 */
export interface ContextMenuStyleConfig {
  /** Panel width in px, or any CSS width. Default `180`. */
  width?: number | string;
  minWidth?: number | string;
  /**
   * Rows to show before the panel scrolls. **Unset by default: the panel sizes to its content** and only
   * scrolls when it cannot fit above or below its anchor in the viewport.
   *
   * This used to default to 6, which meant a seven-item menu clamped and scrolled with most of the
   * window still free. Set it only for a menu long enough that a cap is genuinely kinder than a scroll.
   * Measured from the real rows, so it holds under a `density` / `fontSize` override. Ignored when
   * `maxHeight` is set.
   */
  maxVisibleItems?: number;
  /** Pin the panel height explicitly (px or CSS). Overrides `maxVisibleItems`. */
  maxHeight?: number | string;
  placement?: ContextMenuPlacement;
  /** Gap between trigger and panel in px. Default `4`. */
  offset?: number;
  /** `compact` tightens the row padding for dense grids. Default `comfortable`. */
  density?: 'compact' | 'comfortable';
  /** Default `1000` — above the app chrome. */
  zIndex?: number;
  /** Fade/slide the panel in. Default `true`. */
  animate?: boolean;
  /** Reserve the icon column even for items without an icon, so labels stay aligned. Default `true`. */
  alignLabels?: boolean;
  /** Extra class(es) on the panel — the hook for a feature-specific skin. */
  panelClass?: string;
  /** Extra class(es) on every item. */
  itemClass?: string;
  background?: string;
  borderColor?: string;
  textColor?: string;
  /** Hover background for plain items. Variant items tint by intent — see `variantHoverBackground`. */
  hoverBackground?: string;
  /**
   * Per-variant hover background. Defaults are the brand tint tokens (`--color-light-danger`, …), so a
   * `danger` item hovers red and a `success` item green without any config. The `default` key is the same
   * thing as `hoverBackground` and wins over it.
   */
  variantHoverBackground?: Partial<Record<ContextMenuItemVariant, string>>;
  /**
   * Draw a close button in the corner of a `panelContent` panel.
   *
   * Escape and an outside click always dismiss a panel; this is for one that reads as a CARD rather
   * than as a menu, where neither is discoverable — a reader who has just been shown a panel of
   * figures looks for the way out of it, not for a keyboard shortcut.
   */
  closable?: boolean;
  dangerColor?: string;
  radius?: string;
  fontSize?: string;
  shadow?: string;
}

export interface ContextMenuConfig<T = unknown> {
  items: ContextMenuItem<T>[];
  trigger?: ContextMenuTriggerConfig;
  style?: ContextMenuStyleConfig;
  /** Optional heading inside the panel. */
  header?: string;
  /** Shown when every item is hidden for the current row. Omit to render nothing. */
  emptyText?: string;
  /** Close the panel after a selection. Default `true`. */
  closeOnSelect?: boolean;
  /**
   * Freeze the page behind the open menu — wheel, touch drag and scroll keys are blocked outside the panel
   * (the panel's own list still scrolls). Default `true`. Set `false` to let the page scroll with the menu
   * open, in which case the panel stays glued to its trigger.
   */
  lockScroll?: boolean;
  ariaLabel?: string;
}

/** Emitted by `(itemSelect)`. */
export interface ContextMenuSelectEvent<T = unknown> {
  /** The selected item's `id` — switch on this. */
  id: string;
  item: ContextMenuItem<T>;
  /** The row the menu was opened for. */
  context: T | null;
}

/** Ready-made trigger: the bare kebab used in list rows and grid action cells. */
export const KEBAB_TRIGGER: ContextMenuTriggerConfig = {
  icon: 'more',
  iconSize: 15,
  tooltip: 'More actions',
  variant: 'icon',
  size: 'md',
};

/** Ready-made trigger: a labelled "Actions ⌄" button for detail-page headers. */
export const ACTIONS_TRIGGER: ContextMenuTriggerConfig = {
  label: 'Actions',
  icon: 'settings',
  iconSize: 15,
  variant: 'outline',
  size: 'md',
  showCaret: true,
  tooltip: '',
};

/** Ready-made style: dense panel for grid rows. */
export const COMPACT_MENU_STYLE: ContextMenuStyleConfig = {
  width: 170,
  density: 'compact',
  placement: 'bottom-end',
};
